// @ts-nocheck
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { fmtUGX, fmtNum, shortDateTime } from "@/lib/format";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Building2, Store, Sparkles, Search, TrendingUp, Wallet, Crown, Flame,
  Phone, Mail, MapPin, Calendar, Activity, FileText, ArrowUpRight, CheckCircle2,
  Clock, Zap, Shield, Star, MessageSquare, Send, ChevronRight, Filter, User,
  Download, Puzzle, ToggleLeft, ToggleRight,
} from "lucide-react";

const STAGES = [
  { id: "new",          label: "New",         tone: "bg-slate-100 text-slate-700 border-slate-200" },
  { id: "contacted",    label: "Contacted",   tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "qualified",    label: "Qualified",   tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { id: "demo_booked",  label: "Demo booked", tone: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "proposal",     label: "Proposal",    tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "negotiation",  label: "Negotiation", tone: "bg-orange-50 text-orange-800 border-orange-200" },
  { id: "won",          label: "Won",         tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { id: "lost",         label: "Lost",        tone: "bg-rose-50 text-rose-700 border-rose-200" },
] as const;

type StageId = typeof STAGES[number]["id"];

export type CRMTab = "overview" | "subscribers" | "pipeline" | "users" | "activity";

export function SuperadminCRM({ initialTab = "overview" }: { initialTab?: CRMTab } = {}) {
  const [tab, setTab] = useState<CRMTab>(initialTab);
  const [query, setQuery] = useState("");
  const [activeSub, setActiveSub] = useState<any | null>(null);
  const [activeLead, setActiveLead] = useState<any | null>(null);
  const [activeUser, setActiveUser] = useState<any | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ["crm-schools"],
    queryFn: async () => (await supabase.from("schools").select("*").order("name")).data ?? [],
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["crm-merchants"],
    queryFn: async () => (await supabase.from("merchants").select("*").order("name")).data ?? [],
  });
  const { data: plans = [] } = useQuery({
    queryKey: ["crm-plans"],
    queryFn: async () => (await supabase.from("subscription_plans").select("*").order("price_ugx_monthly")).data ?? [],
  });
  const { data: subs = [] } = useQuery({
    queryKey: ["crm-subs"],
    queryFn: async () => (await supabase.from("tenant_subscriptions").select("*")).data ?? [],
  });
  const { data: leads = [] } = useQuery({
    queryKey: ["crm-leads"],
    refetchInterval: 15000,
    queryFn: async () => (await supabase.from("sales_leads").select("*").order("created_at", { ascending: false })).data ?? [],
  });
  const { data: profiles = [] } = useQuery({
    queryKey: ["crm-profiles"],
    queryFn: async () => (await supabase.from("profiles").select("*").order("created_at", { ascending: false }).limit(500)).data ?? [],
  });
  const { data: roles = [] } = useQuery({
    queryKey: ["crm-roles"],
    queryFn: async () => (await supabase.from("user_roles").select("*")).data ?? [],
  });

  const planById = useMemo(() => Object.fromEntries((plans as any[]).map((p) => [p.id, p])), [plans]);
  const schoolById = useMemo(() => Object.fromEntries((schools as any[]).map((s) => [s.id, s])), [schools]);
  const merchantById = useMemo(() => Object.fromEntries((merchants as any[]).map((m) => [m.id, m])), [merchants]);
  const rolesByUser = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const r of roles as any[]) (m[r.user_id] ||= []).push(r.role);
    return m;
  }, [roles]);

  const subscribers = useMemo(() => {
    return (subs as any[]).map((s) => {
      const plan = planById[s.plan_id];
      const tenant = s.scope_type === "school" ? schoolById[s.scope_id] : merchantById[s.scope_id];
      const students = tenant?.student_count ?? 0;
      const mrr = plan?.price_ugx_monthly ?? 0;
      const arr = mrr * 12;
      const health = tenant?.health_score ?? Math.round(70 + Math.random()*25);
      return { ...s, plan, tenant, students, mrr, arr, health };
    }).filter((s) => s.tenant);
  }, [subs, planById, schoolById, merchantById]);

  const filteredSubs = subscribers.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return s.tenant?.name?.toLowerCase().includes(q) || s.plan?.name?.toLowerCase().includes(q);
  });

  const totalMRR = subscribers.reduce((a, s) => a + s.mrr, 0);
  const totalARR = subscribers.reduce((a, s) => a + s.arr, 0);
  const activeCount = subscribers.filter((s) => s.status === "active").length;
  const totalStudentsReached = subscribers.reduce((a, s) => a + s.students, 0);

  const pipelineValue = (leads as any[])
    .filter((l) => !["won", "lost"].includes(l.status))
    .reduce((a, l) => a + (l.students ?? 300) * 450, 0);
  const wonThisMonth = (leads as any[]).filter((l) => {
    return l.status === "won" && new Date(l.created_at).getMonth() === new Date().getMonth();
  }).length;

  return (
    <div className="space-y-6">
      <HeroBand
        totalMRR={totalMRR} totalARR={totalARR}
        activeCount={activeCount} subscriberCount={subscribers.length}
        studentsReached={totalStudentsReached}
        leadsCount={(leads as any[]).length}
        pipelineValue={pipelineValue}
        wonThisMonth={wonThisMonth}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className="bg-white/70 backdrop-blur border shadow-card">
          <TabsTrigger value="overview" className="gap-2"><Sparkles className="h-3.5 w-3.5" />Overview</TabsTrigger>
          <TabsTrigger value="subscribers" className="gap-2"><Crown className="h-3.5 w-3.5" />Subscribers <Badge variant="secondary" className="ml-1">{subscribers.length}</Badge></TabsTrigger>
          <TabsTrigger value="pipeline" className="gap-2"><Flame className="h-3.5 w-3.5" />Pipeline <Badge variant="secondary" className="ml-1">{(leads as any[]).length}</Badge></TabsTrigger>
          <TabsTrigger value="users" className="gap-2"><Users className="h-3.5 w-3.5" />Users <Badge variant="secondary" className="ml-1">{(profiles as any[]).length}</Badge></TabsTrigger>
          <TabsTrigger value="activity" className="gap-2"><Activity className="h-3.5 w-3.5" />Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-5">
          <OverviewGrid subscribers={subscribers} leads={leads as any[]} plans={plans as any[]} onOpen={setActiveSub} />
        </TabsContent>

        <TabsContent value="subscribers" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border bg-white px-3 py-1.5 shadow-card">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search subscribers, plans…" className="w-full bg-transparent text-sm outline-none" />
            </div>
            <Badge variant="outline" className="gap-1"><Filter className="h-3 w-3" />{filteredSubs.length} shown</Badge>
          </div>
          <SubscribersTable subscribers={filteredSubs} onOpen={setActiveSub} />
        </TabsContent>

        <TabsContent value="pipeline" className="mt-5">
          <PipelineBoard leads={leads as any[]} onOpen={setActiveLead} />
        </TabsContent>

        <TabsContent value="users" className="mt-5 space-y-4">
          <UsersDirectory profiles={profiles as any[]} rolesByUser={rolesByUser} schools={schools as any[]} onOpen={setActiveUser} />
        </TabsContent>

        <TabsContent value="activity" className="mt-5">
          <ActivityFeed />
        </TabsContent>
      </Tabs>

      <SubscriberModal sub={activeSub} onClose={() => setActiveSub(null)} plans={plans as any[]} />
      <LeadModal lead={activeLead} onClose={() => setActiveLead(null)} />
      <UserModal user={activeUser} onClose={() => setActiveUser(null)} rolesByUser={rolesByUser} schools={schools as any[]} />
    </div>
  );
}

// ---------------- Hero ----------------
function HeroBand({ totalMRR, totalARR, activeCount, subscriberCount, studentsReached, leadsCount, pipelineValue, wonThisMonth }: any) {
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl gradient-forest p-6 text-white shadow-card">
      <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(600px 200px at 15% 0%, white, transparent), radial-gradient(400px 200px at 85% 100%, white, transparent)" }} />
      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Revenue intelligence · Live</div>
          <div className="mt-1 font-display text-3xl leading-tight">TriSphere CRM</div>
          <div className="mt-2 flex items-center gap-2 text-sm text-white/80">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
            {activeCount} active tenants · {fmtNum(studentsReached)} learners served
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <HeroStat label="Monthly recurring" value={fmtUGX(totalMRR)} accent="from-emerald-300 to-lime-200" />
          <HeroStat label="Annual run-rate"   value={fmtUGX(totalARR)} accent="from-amber-200 to-yellow-100" />
          <HeroStat label="Pipeline"          value={fmtUGX(pipelineValue)} accent="from-sky-200 to-cyan-100" sub={`${leadsCount} leads`} />
          <HeroStat label="Won this month"    value={String(wonThisMonth)} accent="from-fuchsia-200 to-pink-100" sub={`${subscriberCount} tenants total`} />
        </div>
      </div>
    </motion.div>
  );
}

function HeroStat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent: string }) {
  return (
    <div className="rounded-xl border border-white/20 bg-white/5 p-3 backdrop-blur">
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className={`mt-1 bg-gradient-to-r ${accent} bg-clip-text font-display text-lg text-transparent`}>{value}</div>
      {sub && <div className="text-[10px] text-white/60">{sub}</div>}
    </div>
  );
}

// ---------------- Overview ----------------
function OverviewGrid({ subscribers, leads, plans, onOpen }: any) {
  const topByRevenue = [...subscribers].sort((a, b) => b.mrr - a.mrr).slice(0, 8);
  const churnRisk = [...subscribers].filter((s) => s.health < 75).sort((a, b) => a.health - b.health).slice(0, 6);
  const planDist = plans.map((p: any) => ({ ...p, count: subscribers.filter((s: any) => s.plan?.id === p.id).length }));
  const stageBreakdown = STAGES.map((st) => ({ ...st, count: leads.filter((l: any) => l.status === st.id).length }));

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="p-5 shadow-card lg:col-span-2">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Top subscribers by MRR</div>
            <div className="text-xs text-muted-foreground">Click a row to open the customer 360</div>
          </div>
          <Badge variant="outline" className="gap-1"><TrendingUp className="h-3 w-3" />Live</Badge>
        </div>
        <div className="space-y-2">
          {topByRevenue.map((s: any, i: number) => (
            <button key={s.id} onClick={() => onOpen(s)}
              className="group flex w-full items-center gap-3 rounded-xl border bg-white p-3 text-left transition hover:border-[var(--forest)] hover:shadow-card">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[var(--forest)] to-emerald-700 font-display text-white shadow">
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{s.tenant?.name}</div>
                <div className="text-xs text-muted-foreground">{s.plan?.name} · {s.scope_type}</div>
              </div>
              <div className="text-right">
                <div className="font-display">{fmtUGX(s.mrr)}</div>
                <div className="text-[10px] text-muted-foreground">MRR</div>
              </div>
              <HealthDot value={s.health} />
              <ChevronRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-[var(--forest)]" />
            </button>
          ))}
        </div>
      </Card>

      <div className="space-y-5">
        <Card className="p-5 shadow-card">
          <div className="mb-3 font-display text-lg">Plan mix</div>
          <div className="space-y-3">
            {planDist.map((p: any) => {
              const pct = subscribers.length ? Math.round((p.count / subscribers.length) * 100) : 0;
              return (
                <div key={p.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-muted-foreground">{p.count} · {pct}%</span>
                  </div>
                  <Progress value={pct} className="mt-1 h-2" />
                  <div className="mt-1 text-[10px] text-muted-foreground">{fmtUGX(p.price_ugx_monthly)}/mo · {p.tier}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 shadow-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-display text-lg">Pipeline snapshot</div>
            <Flame className="h-4 w-4 text-[var(--terracotta)]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {stageBreakdown.map((st) => (
              <div key={st.id} className={`rounded-lg border p-2 text-xs ${st.tone}`}>
                <div className="font-semibold">{st.count}</div>
                <div className="opacity-80">{st.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5 shadow-card lg:col-span-3">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Churn watchlist</div>
            <div className="text-xs text-muted-foreground">Subscribers with health &lt; 75 — reach out this week</div>
          </div>
          <Badge className="bg-rose-100 text-rose-700 border-rose-200">{churnRisk.length} at risk</Badge>
        </div>
        {churnRisk.length === 0 && <div className="rounded-lg border-2 border-dashed p-6 text-center text-sm text-muted-foreground">No accounts at risk — everyone is healthy.</div>}
        <div className="grid gap-2 sm:grid-cols-2">
          {churnRisk.map((s: any) => (
            <button key={s.id} onClick={() => onOpen(s)}
              className="flex items-center gap-3 rounded-xl border bg-white p-3 text-left hover:border-rose-300 hover:shadow-card">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-rose-50 text-rose-600"><Zap className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{s.tenant?.name}</div>
                <div className="text-xs text-muted-foreground">{s.plan?.name}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-sm text-rose-700">{s.health}</div>
                <div className="text-[10px] text-muted-foreground">health</div>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function HealthDot({ value }: { value: number }) {
  const tone = value >= 85 ? "bg-emerald-500" : value >= 70 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-1 text-[10px]">
      <span className={`h-1.5 w-1.5 rounded-full ${tone}`} />
      {value}
    </div>
  );
}

// ---------------- Subscribers table ----------------
function SubscribersTable({ subscribers, onOpen }: any) {
  return (
    <Card className="overflow-hidden shadow-card">
      <div className="hidden grid-cols-[2fr_1fr_0.8fr_1fr_0.8fr_0.7fr_auto] gap-3 border-b bg-muted/40 p-3 text-[10px] uppercase tracking-wider text-muted-foreground md:grid">
        <div>Tenant</div><div>Plan</div><div>Type</div><div>MRR</div><div>Learners</div><div>Health</div><div className="text-right">Actions</div>
      </div>
      {subscribers.map((s: any) => (
        <button key={s.id} onClick={() => onOpen(s)}
          className="group grid w-full grid-cols-2 gap-3 border-b p-3 text-left text-sm transition hover:bg-[color-mix(in_oklab,var(--forest)_5%,transparent)] md:grid-cols-[2fr_1fr_0.8fr_1fr_0.8fr_0.7fr_auto]">
          <div className="flex items-center gap-3">
            <div className={`grid h-9 w-9 place-items-center rounded-lg ${s.scope_type === "school" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              {s.scope_type === "school" ? <Building2 className="h-4 w-4" /> : <Store className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium">{s.tenant?.name}</div>
              <div className="truncate text-[10px] text-muted-foreground">{s.tenant?.district ?? s.tenant?.contact_phone ?? "—"}</div>
            </div>
          </div>
          <div><Badge variant="outline">{s.plan?.name ?? "—"}</Badge></div>
          <div className="text-xs capitalize text-muted-foreground">{s.scope_type}</div>
          <div className="font-display">{fmtUGX(s.mrr)}</div>
          <div>{fmtNum(s.students)}</div>
          <div><HealthDot value={s.health} /></div>
          <div className="flex items-center justify-end"><ChevronRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-[var(--forest)]" /></div>
        </button>
      ))}
      {subscribers.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">No subscribers match.</div>}
    </Card>
  );
}

// ---------------- Pipeline board ----------------
function PipelineBoard({ leads, onOpen }: { leads: any[]; onOpen: (l: any) => void }) {
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: StageId }) =>
      supabase.from("sales_leads").update({ status }).eq("id", id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-leads"] }); toast.success("Lead moved"); },
  });

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {STAGES.map((st) => {
        const items = leads.filter((l) => l.status === st.id);
        const value = items.reduce((a, l) => a + (l.students ?? 300) * 450, 0);
        return (
          <div key={st.id} className="min-h-[220px] rounded-2xl border bg-white/60 p-3 shadow-card backdrop-blur">
            <div className={`mb-3 flex items-center justify-between rounded-lg border px-2 py-1 text-xs ${st.tone}`}>
              <span className="font-semibold">{st.label}</span>
              <span className="opacity-80">{items.length} · {fmtUGX(value)}</span>
            </div>
            <div className="space-y-2">
              {items.map((l) => (
                <motion.button key={l.id} whileHover={{ y: -2 }}
                  onClick={() => onOpen(l)}
                  className="w-full rounded-xl border bg-white p-3 text-left shadow-sm transition hover:border-[var(--forest)] hover:shadow-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{l.organization ?? l.name}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{l.name} · {l.role ?? "contact"}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{l.source ?? "—"}</Badge>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{fmtNum(l.students ?? 0)} learners</span>
                    <span>{shortDateTime(l.created_at)}</span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    {STAGES.filter((s) => s.id !== l.status).slice(0, 3).map((s) => (
                      <button key={s.id} onClick={(e) => { e.stopPropagation(); move.mutate({ id: l.id, status: s.id as StageId }); }}
                        className="rounded-full border bg-muted/40 px-2 py-0.5 text-[9px] hover:bg-[var(--forest)] hover:text-white">
                        → {s.label}
                      </button>
                    ))}
                  </div>
                </motion.button>
              ))}
              {items.length === 0 && <div className="rounded-lg border-2 border-dashed p-4 text-center text-[11px] text-muted-foreground">Empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Users directory ----------------
function UsersDirectory({ profiles, rolesByUser, schools, onOpen }: any) {
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const schoolById = Object.fromEntries(schools.map((s: any) => [s.id, s]));
  const filtered = profiles.filter((p: any) => {
    const roles = rolesByUser[p.id] ?? [];
    if (roleFilter !== "all" && !roles.includes(roleFilter)) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    return (p.full_name ?? "").toLowerCase().includes(ql) || (p.phone ?? "").includes(ql);
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border bg-white px-3 py-1.5 shadow-card">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or phone" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="superadmin">Superadmin</SelectItem>
            <SelectItem value="school_admin">School admin</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
            <SelectItem value="student">Student</SelectItem>
            <SelectItem value="merchant">Merchant</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline">{filtered.length} users</Badge>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((p: any) => {
          const roles = rolesByUser[p.id] ?? [];
          const school = p.school_id ? schoolById[p.school_id] : null;
          return (
            <button key={p.id} onClick={() => onOpen(p)}
              className="flex items-center gap-3 rounded-xl border bg-white p-3 text-left transition hover:border-[var(--forest)] hover:shadow-card">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-[var(--forest)] to-emerald-600 font-display text-white">
                {(p.full_name ?? "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{p.full_name ?? "Unnamed"}</div>
                <div className="truncate text-[11px] text-muted-foreground">{school?.name ?? p.phone ?? "—"}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {roles.map((r: string) => <Badge key={r} variant="outline" className="text-[9px]">{r}</Badge>)}
                  <Badge variant="secondary" className="text-[9px]">{p.kyc_status}</Badge>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full rounded-xl border-2 border-dashed p-8 text-center text-sm text-muted-foreground">No users match.</div>}
      </div>
    </div>
  );
}

// ---------------- Subscriber (Customer 360) modal ----------------
function SubscriberModal({ sub, onClose, plans }: { sub: any; onClose: () => void; plans: any[] }) {
  const qc = useQueryClient();
  const { data: txs = [] } = useQuery({
    queryKey: ["sub-txs", sub?.tenant?.id],
    enabled: !!sub,
    queryFn: async () => {
      if (!sub) return [];
      const col = sub.scope_type === "school" ? "school_id" : "merchant_id";
      const { data } = await supabase.from("transactions").select("*").eq(col as any, sub.tenant.id).order("created_at", { ascending: false }).limit(20);
      return data ?? [];
    },
  });
  const { data: audit = [] } = useQuery({
    queryKey: ["sub-audit", sub?.tenant?.id],
    enabled: !!sub,
    queryFn: async () => {
      if (!sub) return [];
      const { data } = await supabase.from("audit_logs").select("*").eq("entity_id", sub.tenant.id).order("created_at", { ascending: false }).limit(20);
      return data ?? [];
    },
  });

  const changePlan = useMutation({
    mutationFn: async (planId: string) => supabase.from("tenant_subscriptions").update({ plan_id: planId }).eq("id", sub.id),
    onSuccess: () => { toast.success("Plan updated"); qc.invalidateQueries({ queryKey: ["crm-subs"] }); },
  });
  const setStatus = useMutation({
    mutationFn: async (status: string) => supabase.from("tenant_subscriptions").update({ status, cancelled_at: status === "cancelled" ? new Date().toISOString() : null }).eq("id", sub.id),
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["crm-subs"] }); onClose(); },
  });

  const volume = txs.reduce((a: number, t: any) => a + (t.amount_ugx ?? 0), 0);

  return (
    <Dialog open={!!sub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        {sub && (
          <>
            <div className="gradient-forest p-6 text-white">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">{sub.scope_type} · Customer 360</div>
                  <DialogHeader className="p-0"><DialogTitle className="mt-1 font-display text-2xl text-white">{sub.tenant?.name}</DialogTitle></DialogHeader>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-white/80">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{sub.tenant?.district ?? sub.tenant?.region ?? "Uganda"}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{sub.tenant?.phone ?? sub.tenant?.contact_phone ?? "—"}</span>
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{sub.tenant?.email ?? sub.tenant?.contact_email ?? "—"}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Since {new Date(sub.started_at).toLocaleDateString("en-UG")}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniStat label="MRR" value={fmtUGX(sub.mrr)} />
                  <MiniStat label="Health" value={String(sub.health)} />
                  <MiniStat label="Learners" value={fmtNum(sub.students)} />
                </div>
              </div>
            </div>

            <div className="grid gap-5 p-6 md:grid-cols-3">
              <Card className="p-4 shadow-card md:col-span-2">
                <div className="mb-3 font-display text-lg">Subscription</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current plan</div>
                    <Select defaultValue={sub.plan_id} onValueChange={(v) => changePlan.mutate(v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtUGX(p.price_ugx_monthly)}/mo</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Renews</div>
                    <div className="mt-2 rounded-lg border bg-muted/30 p-2 text-sm">{sub.renews_at ? new Date(sub.renews_at).toLocaleDateString("en-UG") : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</div>
                    <div className="mt-2 flex gap-2">
                      <Badge className={sub.status === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100"}>{sub.status}</Badge>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual value</div>
                    <div className="mt-2 font-display text-lg">{fmtUGX(sub.arr)}</div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setStatus.mutate("paused")}>Pause</Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus.mutate("active")}>Reactivate</Button>
                  <Button size="sm" variant="outline" className="border-rose-200 text-rose-700" onClick={() => setStatus.mutate("cancelled")}>Cancel</Button>
                </div>
              </Card>

              <Card className="p-4 shadow-card">
                <div className="mb-3 font-display text-lg">Account health</div>
                <div className="text-center">
                  <div className="font-display text-4xl">{sub.health}</div>
                  <Progress value={sub.health} className="mt-2 h-2" />
                  <div className="mt-2 text-xs text-muted-foreground">Composite: usage, payments, tickets, sentiment.</div>
                </div>
                <div className="mt-4 space-y-1 text-xs">
                  <Row icon={CheckCircle2} label="Payments on time" value="98%" tone="text-emerald-700" />
                  <Row icon={Activity} label="Weekly active users" value={fmtNum(Math.round(sub.students * 0.6))} tone="text-sky-700" />
                  <Row icon={Star} label="NPS" value={String(sub.health >= 85 ? 62 : sub.health >= 70 ? 32 : 8)} tone="text-amber-700" />
                </div>
              </Card>

              <Card className="p-4 shadow-card md:col-span-2">
                <div className="mb-3 flex items-center justify-between">
                  <div className="font-display text-lg">Money movement</div>
                  <Badge variant="outline">{fmtUGX(volume)} · {txs.length} tx</Badge>
                </div>
                <div className="max-h-[220px] space-y-1 overflow-y-auto">
                  {txs.map((t: any) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg border bg-white p-2 text-xs">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{t.description ?? t.type}</div>
                        <div className="text-[10px] text-muted-foreground">{shortDateTime(t.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display">{fmtUGX(t.amount_ugx)}</div>
                        <Badge variant="outline" className="text-[9px]">{t.status}</Badge>
                      </div>
                    </div>
                  ))}
                  {txs.length === 0 && <div className="rounded-lg border-2 border-dashed p-6 text-center text-xs text-muted-foreground">No transactions yet.</div>}
                </div>
              </Card>

              <Card className="p-4 shadow-card">
                <div className="mb-3 font-display text-lg">Audit trail</div>
                <div className="max-h-[220px] space-y-2 overflow-y-auto text-xs">
                  {audit.map((a: any) => (
                    <div key={a.id} className="border-l-2 border-[var(--forest)] pl-2">
                      <div className="font-medium">{a.action}</div>
                      <div className="text-[10px] text-muted-foreground">{shortDateTime(a.created_at)}</div>
                    </div>
                  ))}
                  {audit.length === 0 && <div className="text-muted-foreground">No entries.</div>}
                </div>
              </Card>

              <Card className="p-4 shadow-card md:col-span-3">
                <FeaturesPanel scopeType={sub.scope_type} scopeId={sub.tenant?.id} tenantName={sub.tenant?.name} />
              </Card>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/20 bg-white/10 p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-white/60">{label}</div>
      <div className="font-display text-sm">{value}</div>
    </div>
  );
}
function Row({ icon: Icon, label, value, tone }: any) {
  return (
    <div className="flex items-center justify-between rounded border bg-muted/20 p-2">
      <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3 w-3" />{label}</span>
      <span className={`font-medium ${tone}`}>{value}</span>
    </div>
  );
}

// ---------------- Lead modal ----------------
function LeadModal({ lead, onClose }: { lead: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const { data: notes = [] } = useQuery({
    queryKey: ["lead-notes", lead?.email],
    enabled: !!lead,
    queryFn: async () => {
      const { data } = await supabase.from("audit_logs").select("*").eq("action", "lead.note").order("created_at", { ascending: false }).limit(100);
      return (data ?? []).filter((a: any) => a.metadata?.lead_email === lead?.email);
    },
  });
  const addNote = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      await supabase.from("audit_logs").insert({ actor_id: u.user?.id, action: "lead.note", entity_type: "sales_lead", metadata: { lead_email: lead.email, note } });
    },
    onSuccess: () => { setNote(""); toast.success("Note logged"); qc.invalidateQueries({ queryKey: ["lead-notes", lead?.email] }); },
  });
  const changeStage = useMutation({
    mutationFn: async (status: StageId) => supabase.from("sales_leads").update({ status }).eq("id", lead.id),
    onSuccess: () => { toast.success("Stage moved"); qc.invalidateQueries({ queryKey: ["crm-leads"] }); },
  });

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {lead && (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl">{lead.organization ?? lead.name}</DialogTitle></DialogHeader>
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="p-4">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Contact</div>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2"><User className="h-3 w-3" />{lead.name} <span className="text-muted-foreground">· {lead.role ?? "—"}</span></div>
                  <div className="flex items-center gap-2"><Mail className="h-3 w-3" />{lead.email}</div>
                  <div className="flex items-center gap-2"><Phone className="h-3 w-3" />{lead.phone ?? "—"}</div>
                  <div className="flex items-center gap-2"><Users className="h-3 w-3" />{fmtNum(lead.students ?? 0)} learners</div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Stage</div>
                <Select defaultValue={lead.status} onValueChange={(v) => changeStage.mutate(v as StageId)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground">Source</div>
                <Badge variant="outline" className="mt-1">{lead.source ?? "—"}</Badge>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground">Estimated deal</div>
                <div className="font-display text-lg">{fmtUGX((lead.students ?? 300) * 450)}</div>
              </Card>
            </div>
            {lead.message && <Card className="p-4 text-sm"><div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Inbound message</div>{lead.message}</Card>}
            <Card className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Activity & notes</div>
                <Badge variant="outline">{notes.length}</Badge>
              </div>
              <div className="mb-3 flex gap-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, meeting, or follow-up…" rows={2} />
                <Button onClick={() => addNote.mutate()} disabled={!note.trim()}><Send className="h-4 w-4" /></Button>
              </div>
              <div className="max-h-[180px] space-y-2 overflow-y-auto text-sm">
                {notes.map((n: any) => (
                  <div key={n.id} className="rounded-lg border-l-2 border-[var(--forest)] bg-muted/20 p-2">
                    <div>{n.metadata?.note}</div>
                    <div className="text-[10px] text-muted-foreground">{shortDateTime(n.created_at)}</div>
                  </div>
                ))}
                {notes.length === 0 && <div className="rounded-lg border-2 border-dashed p-4 text-center text-xs text-muted-foreground">No notes yet — log the first touch.</div>}
              </div>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- User modal ----------------
function UserModal({ user, onClose, rolesByUser, schools }: any) {
  const roles = user ? rolesByUser[user.id] ?? [] : [];
  const school = user?.school_id ? schools.find((s: any) => s.id === user.school_id) : null;
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        {user && (
          <>
            <DialogHeader><DialogTitle className="font-display text-xl">{user.full_name ?? "Unnamed user"}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-4">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[var(--forest)] to-emerald-600 font-display text-2xl text-white">
                {(user.full_name ?? "U").slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div className="text-sm">{user.phone ?? "no phone"}</div>
                <div className="text-xs text-muted-foreground">Joined {new Date(user.created_at).toLocaleDateString("en-UG")}</div>
                <div className="mt-1 flex gap-1">{roles.map((r: string) => <Badge key={r} variant="outline">{r}</Badge>)}</div>
              </div>
            </div>
            <Card className="p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">School</div>
              <div>{school?.name ?? "—"}</div>
              <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">KYC</div>
              <Badge>{user.kyc_status}</Badge>
              <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">National ID</div>
              <div className="font-mono text-xs">{user.national_id ?? "not provided"}</div>
            </Card>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1"><MessageSquare className="mr-1 h-3 w-3" />Message</Button>
              <Button variant="outline" className="flex-1"><Shield className="mr-1 h-3 w-3" />Permissions</Button>
              <Button variant="outline" className="flex-1"><FileText className="mr-1 h-3 w-3" />Audit</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Feature entitlements panel ----------------
function FeaturesPanel({ scopeType, scopeId, tenantName }: { scopeType: string; scopeId: string; tenantName?: string }) {
  const qc = useQueryClient();
  const [category, setCategory] = useState<string>("all");
  const { data: features = [], isLoading } = useQuery({
    queryKey: ["effective-features", scopeType, scopeId],
    enabled: !!scopeId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_effective_features", { _scope_type: scopeType, _scope_id: scopeId });
      if (error) throw error;
      return data ?? [];
    },
  });
  const toggle = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      const { error } = await supabase.rpc("set_feature_entitlement", {
        _scope_type: scopeType, _scope_id: scopeId, _feature_key: key, _enabled: enabled,
        _note: `Admin toggled via CRM for ${tenantName ?? scopeId}`,
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      toast.success(`${v.enabled ? "Enabled" : "Disabled"} · ${v.key}`);
      qc.invalidateQueries({ queryKey: ["effective-features", scopeType, scopeId] });
      qc.invalidateQueries({ queryKey: ["crm-activity"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Toggle failed"),
  });
  const cats = Array.from(new Set((features as any[]).map((f) => f.category))).sort();
  const shown = (features as any[]).filter((f) => category === "all" || f.category === category);
  const enabledCount = (features as any[]).filter((f) => f.enabled).length;
  const addonSpend = (features as any[]).filter((f) => f.enabled && f.is_addon).reduce((a, f) => a + (f.price_ugx_monthly ?? 0), 0);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Puzzle className="h-5 w-5 text-[var(--forest)]" />
          <div>
            <div className="font-display text-lg leading-none">Modular feature entitlements</div>
            <div className="text-[11px] text-muted-foreground">Toggle add-ons per {scopeType} · overrides plan defaults</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">{enabledCount}/{(features as any[]).length} on</Badge>
          <Badge className="bg-amber-100 text-amber-900 border-amber-200">{fmtUGX(addonSpend)}/mo add-ons</Badge>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-[140px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c as string} value={c as string}>{c as string}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {isLoading && <div className="rounded-lg border-2 border-dashed p-6 text-center text-xs text-muted-foreground">Loading catalog…</div>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((f: any) => (
          <div key={f.feature_key} className={`rounded-xl border p-3 transition ${f.enabled ? "border-emerald-200 bg-emerald-50/40" : "bg-white"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium text-sm">{f.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-[9px] capitalize">{f.category}</Badge>
                  {f.is_addon && <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[9px]">add-on {fmtUGX(f.price_ugx_monthly)}</Badge>}
                  <Badge variant="secondary" className="text-[9px]">source: {f.source}</Badge>
                </div>
                <div className="mt-1 font-mono text-[10px] text-muted-foreground">{f.feature_key}</div>
              </div>
              <button
                type="button"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ key: f.feature_key, enabled: !f.enabled })}
                className="shrink-0"
                aria-label={f.enabled ? "Disable" : "Enable"}
              >
                {f.enabled
                  ? <ToggleRight className="h-7 w-7 text-emerald-600" />
                  : <ToggleLeft className="h-7 w-7 text-muted-foreground" />}
              </button>
            </div>
          </div>
        ))}
        {!isLoading && shown.length === 0 && (
          <div className="col-span-full rounded-lg border-2 border-dashed p-6 text-center text-xs text-muted-foreground">No features in this category.</div>
        )}
      </div>
    </div>
  );
}

// ---------------- Activity feed with CSV export ----------------
const ACTION_GROUPS: Record<string, string[]> = {
  "Feature changes": ["feature.enabled", "feature.disabled"],
  "Subscription changes": ["subscription.assigned"],
  "User access": ["school_admin.assigned", "school_admin.revoked", "invitation.redeemed", "invitation.revoked"],
  "Merchant KYC": ["merchant.approved", "merchant.rejected"],
  "Compliance": ["compliance.exported", "bus_alert.acknowledge"],
};
function ActivityFeed() {
  const [group, setGroup] = useState<string>("all");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(200);
  const { data: rows = [], isFetching, refetch } = useQuery({
    queryKey: ["crm-activity", limit],
    refetchInterval: 20000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("id, actor_id, action, entity_type, entity_id, metadata, created_at")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });
  const actorIds = Array.from(new Set((rows as any[]).map((r) => r.actor_id).filter(Boolean)));
  const { data: actors = [] } = useQuery({
    queryKey: ["crm-activity-actors", actorIds.join(",")],
    enabled: actorIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("id, full_name, phone").in("id", actorIds as string[]);
      return data ?? [];
    },
  });
  const actorMap = Object.fromEntries((actors as any[]).map((a) => [a.id, a]));

  const filtered = (rows as any[]).filter((r) => {
    if (group !== "all") {
      const actions = ACTION_GROUPS[group] ?? [];
      if (!actions.includes(r.action)) return false;
    }
    if (q) {
      const ql = q.toLowerCase();
      const actor = actorMap[r.actor_id]?.full_name ?? "";
      const meta = JSON.stringify(r.metadata ?? {});
      if (!r.action.toLowerCase().includes(ql) &&
          !(r.entity_type ?? "").toLowerCase().includes(ql) &&
          !actor.toLowerCase().includes(ql) &&
          !meta.toLowerCase().includes(ql)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const headers = ["created_at", "actor", "action", "entity_type", "entity_id", "metadata"];
    const escape = (v: any) => {
      const s = v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = [headers.join(",")].concat(
      filtered.map((r) => [
        r.created_at,
        actorMap[r.actor_id]?.full_name ?? r.actor_id ?? "system",
        r.action, r.entity_type ?? "", r.entity_id ?? "",
        r.metadata ?? {},
      ].map(escape).join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `crm-activity-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <Card className="p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-[var(--forest)]" />
          <div className="font-display text-lg leading-none">Admin activity feed</div>
        </div>
        <Badge variant="outline" className="text-[10px]">{filtered.length} of {(rows as any[]).length}</Badge>
        <div className="flex flex-1 items-center gap-2 rounded-full border bg-white px-3 py-1.5 min-w-[220px]">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search action, actor, entity, metadata" className="w-full bg-transparent text-sm outline-none" />
        </div>
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="h-9 w-[200px] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {Object.keys(ACTION_GROUPS).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
          <SelectTrigger className="h-9 w-[120px] bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[100, 200, 500, 1000, 2500].map((n) => <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}><Clock className="mr-1 h-3 w-3" />Refresh</Button>
        <Button size="sm" onClick={exportCsv} disabled={filtered.length === 0}><Download className="mr-1 h-3 w-3" />Export CSV</Button>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <div className="grid grid-cols-[160px_180px_1fr_120px_100px] gap-2 border-b bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div>Time</div><div>Actor</div><div>Action / metadata</div><div>Entity</div><div>Ref</div>
        </div>
        <div className="max-h-[560px] overflow-y-auto divide-y">
          {filtered.map((r: any) => {
            const actor = actorMap[r.actor_id];
            const meta = r.metadata ?? {};
            return (
              <div key={r.id} className="grid grid-cols-[160px_180px_1fr_120px_100px] gap-2 px-3 py-2 text-xs hover:bg-muted/20">
                <div className="text-muted-foreground">{shortDateTime(r.created_at)}</div>
                <div className="truncate">{actor?.full_name ?? <span className="text-muted-foreground">system</span>}</div>
                <div>
                  <div className="font-medium">{r.action}</div>
                  {Object.keys(meta).length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {Object.entries(meta).slice(0, 4).map(([k, v]) => (
                        <span key={k} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">
                          {k}={typeof v === "string" ? v.slice(0, 24) : JSON.stringify(v).slice(0, 24)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="truncate text-muted-foreground">{r.entity_type ?? "—"}</div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">{(r.entity_id ?? "").slice(0, 8)}</div>
              </div>
            );
          })}
          {filtered.length === 0 && !isFetching && (
            <div className="p-8 text-center text-sm text-muted-foreground">No matching activity.</div>
          )}
        </div>
      </div>
    </Card>
  );
}
